import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSubscriptionPlans } from '../contexts/SubscriptionPlansContext';
import { Crown, Server, CheckCircle, ArrowRight } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import { useNavigate } from 'react-router-dom';

const Subscription = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Get available server subscription plans from context
  const { plans, isLoading: plansLoading } = useSubscriptionPlans();

  const handleGoToServerManagement = () => {
    navigate('/servers');
  };

  if (plansLoading) {
    return <LoadingSpinner size="xl" />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-discord-lightest mb-2">
              💳 Subscription Plans
            </h1>
            <p className="text-discord-light">
              Choose the perfect plan for your Discord servers and unlock premium games.
            </p>
          </div>
          
          <div className="flex items-center space-x-2">
            <Crown className="h-8 w-8 text-discord-yellow" />
          </div>
        </div>
      </div>

      {/* Current Status */}
      <div className="card">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-discord-lightest mb-2">
              🎮 Ainixit BattleBot Subscriptions
            </h2>
            <p className="text-discord-light mb-4">
              Our subscription system is now server-based! Each Discord server can have its own subscription plan.
            </p>
          </div>
        </div>
        
        <div className="bg-discord-blurple/10 border border-discord-blurple/20 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <Server className="h-6 w-6 text-discord-blurple" />
            <div>
              <h3 className="font-semibold text-discord-lightest">Server-Based Subscriptions</h3>
              <p className="text-sm text-discord-light">
                Manage subscriptions for each Discord server individually. Perfect for communities and gaming groups.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Available Plans */}
      <div className="grid md:grid-cols-2 gap-6">
        {plans.map((plan) => (
          <div key={plan.id} className="card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className={`p-2 rounded-lg ${
                  plan.id === 'free' 
                    ? 'bg-discord-dark text-discord-light' 
                    : 'bg-discord-blurple text-white'
                }`}>
                  <Crown className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-discord-lightest">{plan.name}</h3>
                  <p className="text-discord-light">
                    {plan.id === 'free' ? 'Perfect for getting started' : 'Unlock all premium games'}
                  </p>
                </div>
              </div>
              
              <div className="text-right">
                <div className="text-2xl font-bold text-discord-lightest">
                  {plan.id === 'free' ? 'Free' : `$${(plan.price / 100).toFixed(2)}`}
                </div>
                <div className="text-sm text-discord-light">
                  {plan.id === 'free' ? 'Forever' : `per ${plan.interval}`}
                </div>
              </div>
            </div>

            {/* Available Games */}
            <div className="mb-4">
              <h4 className="font-semibold text-discord-lightest mb-2">Available Games:</h4>
              <div className="flex flex-wrap gap-2">
                {plan.availableGames?.map((game) => (
                  <span key={game} className="px-3 py-1 bg-discord-blurple/20 text-discord-blurple text-sm rounded-full">
                    {game.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </span>
                ))}
              </div>
            </div>

            {/* Features */}
            <div className="space-y-2 mb-6">
              {Object.entries(plan.features || {}).map(([key, value]) => (
                <div key={key} className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-400 flex-shrink-0" />
                  <span className="text-discord-light text-sm capitalize">
                    {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                  </span>
                </div>
              ))}
            </div>

            <button
              onClick={handleGoToServerManagement}
              disabled={plan.id === 'free'}
              className={`w-full py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2 ${
                plan.id === 'free' 
                  ? 'bg-discord-dark text-discord-light cursor-not-allowed' 
                  : 'bg-discord-blurple hover:bg-discord-blurple/90 text-white'
              }`}
            >
              <span>{plan.id === 'free' ? 'Current Plan' : 'Manage Server Subscriptions'}</span>
              {plan.id !== 'free' && <ArrowRight className="h-4 w-4" />}
            </button>
          </div>
        ))}
      </div>

      {/* Server Management CTA */}
      <div className="card">
        <div className="text-center">
          <Server className="h-12 w-12 text-discord-blurple mx-auto mb-4" />
          <h3 className="text-xl font-bold text-discord-lightest mb-2">
            Ready to Upgrade Your Servers?
          </h3>
          <p className="text-discord-light mb-6">
            Go to Server Management to subscribe individual Discord servers to premium plans and unlock all games.
          </p>
          <button
            onClick={handleGoToServerManagement}
            className="bg-discord-blurple hover:bg-discord-blurple/90 text-white py-3 px-6 rounded-lg font-medium transition-colors flex items-center space-x-2 mx-auto"
          >
            <Server className="h-5 w-5" />
            <span>Go to Server Management</span>
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* How It Works */}
      <div className="card">
        <h3 className="text-xl font-bold text-discord-lightest mb-4">How Server Subscriptions Work</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="text-center">
            <div className="bg-discord-blurple/20 rounded-full p-3 w-12 h-12 mx-auto mb-3 flex items-center justify-center">
              <span className="text-discord-blurple font-bold">1</span>
            </div>
            <h4 className="font-semibold text-discord-lightest mb-2">Choose Your Servers</h4>
            <p className="text-sm text-discord-light">
              Select which Discord servers you want to upgrade to premium.
            </p>
          </div>
          
          <div className="text-center">
            <div className="bg-discord-blurple/20 rounded-full p-3 w-12 h-12 mx-auto mb-3 flex items-center justify-center">
              <span className="text-discord-blurple font-bold">2</span>
            </div>
            <h4 className="font-semibold text-discord-lightest mb-2">Subscribe & Pay</h4>
            <p className="text-sm text-discord-light">
              Pay $5/month per server through our secure Stripe integration.
            </p>
          </div>
          
          <div className="text-center">
            <div className="bg-discord-blurple/20 rounded-full p-3 w-12 h-12 mx-auto mb-3 flex items-center justify-center">
              <span className="text-discord-blurple font-bold">3</span>
            </div>
            <h4 className="font-semibold text-discord-lightest mb-2">Unlock Premium Games</h4>
            <p className="text-sm text-discord-light">
              Instantly access all games including Rock Paper Scissors and future releases.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Subscription;